import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Smartphone } from 'lucide-react';
import { APP_STORE_LIVE } from '../lib/appConfig';

const STORE_URLS = {
  ios: 'https://apps.apple.com/app/checkcheck/id6744003498',
  android: 'https://play.google.com/store/apps/details?id=be.checkcheck.app',
};

// GOOGLE PLAY = DÉSACTIVÉ JUSQU'À PUBLICATION RÉELLE (Tâche 10-11).
// Repasser à true UNIQUEMENT quand l'app Android est publiée sur Google Play.
const ANDROID_PUBLISHED = false;

export const PWAInstallPrompt = () => {
  const { lang } = useLanguage();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!APP_STORE_LIVE && !ANDROID_PUBLISHED) return;
    if (localStorage.getItem('ccc_app_dismissed')) return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.Capacitor && window.Capacitor.isNativePlatform()) return;

    const timer = setTimeout(() => {
      if (!document.querySelector('[data-testid="suggestions-dropdown"]')) setShow(true);
    }, 3000);
    const hide = () => setShow(false);
    const onScroll = () => { if (window.scrollY > 250) hide(); };
    window.addEventListener('cc:hide-pwa-prompt', hide);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('cc:hide-pwa-prompt', hide);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('ccc_app_dismissed', '1');
  };

  if (!show || (!APP_STORE_LIVE && !ANDROID_PUBLISHED)) return null;

  const t = {
    fr: {
      title: 'CheckCheck est disponible en app !',
      subtitle: 'Disponible gratuitement sur l\u2019App Store',
      dismiss: 'Plus tard',
    },
    nl: {
      title: 'CheckCheck is beschikbaar als app!',
      subtitle: 'Gratis beschikbaar in de App Store',
      dismiss: 'Later',
    },
  }[lang];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up" data-testid="app-store-prompt">
      <div className="max-w-md mx-auto bg-white border border-[#2563EB]/30 rounded-2xl p-5 shadow-2xl shadow-slate-300/60">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2563EB]/10 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-[#2563EB]" />
            </div>
            <div>
              <h3 className="font-bold text-[#172033] text-sm font-['Outfit']">{t.title}</h3>
              <p className="text-xs text-slate-500">{t.subtitle}</p>
            </div>
          </div>
          <button onClick={handleDismiss} className="p-1 hover:bg-slate-100 rounded-lg" data-testid="app-store-dismiss">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="flex gap-3 mb-3">
          {APP_STORE_LIVE && (
          <a
            href={STORE_URLS.ios}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#2563EB] text-white font-bold rounded-xl text-xs hover:bg-[#1D4ED8] transition-colors text-center"
            data-testid="app-store-ios-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            App Store
          </a>
          )}
          {ANDROID_PUBLISHED && (
            <a
              href={STORE_URLS.android}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#2563EB] text-white font-bold rounded-xl text-xs hover:bg-[#1D4ED8] transition-colors text-center"
              data-testid="app-store-android-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.523 2.246l1.378 2.386a.25.25 0 0 1-.433.25l-1.394-2.413a8.65 8.65 0 0 0-3.088-1.192A8.65 8.65 0 0 0 12 1a8.65 8.65 0 0 0-1.986.277 8.65 8.65 0 0 0-3.088 1.192L5.532 4.882a.25.25 0 0 1-.433-.25l1.378-2.386C3.357 4.062 1 7.274 1 11h22c0-3.726-2.357-6.938-5.477-8.754zM7 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm10 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM1 12v8a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2v-8H1z"/></svg>
              Google Play
            </a>
          )}
        </div>

        <button
          onClick={handleDismiss}
          className="w-full text-center text-xs text-slate-400 hover:text-slate-600 transition-colors"
          data-testid="app-store-later-btn"
        >
          {t.dismiss}
        </button>
      </div>
    </div>
  );
};
